import React, { useState } from 'react'
import HeadingTitle from '../../../../../components/HeadingTitle'
import { Link } from 'react-router-dom'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { List, ListItem, IconButton, Stack, Typography, Paper, Divider, CircularProgress, Box, TextField } from '@mui/material';

import VisibilityIcon from '@mui/icons-material/Visibility';
import GetAppIcon from '@mui/icons-material/GetApp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useUnitDocuments } from '../../../../../reactQuery/document';

import docIcon from '../../../../../styles/svg/document-list.svg';
import useMutate from '../../../../../hooks/useMutate';
import { deleteDocument, updateDocumentTitle } from '../../../../../sevices/documentApi';
import useDownloader from 'react-use-downloader';
import LongFormDialog from '../../../../../components/Dialog/LongFormDialog';
import ViewFile from '../../../../../components/ViewFile';
import ConfirmationDialog from '../../../../../components/Dialog/ConfirmationDialog';
import moment from 'moment';

function ViewDocuments({ unitId }) {
  const [selectedDocument, setSelectedDocument] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [viewDocumentPopup, setViewDocumentPopup] = useState(false);
  const [currentViewDoc, setCurrentViewDoc] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const { download } = useDownloader();
  
  // Fetch unit documents using React Query
  const { 
    responseData: documents, 
    isLoading, 
    isError, 
    error 
  } = useUnitDocuments(unitId, {}, `unitDocuments_${unitId}`);

  
//   // Create the mutation
  const { mutate: updateDocumentTitleFn } = useMutate(
    updateDocumentTitle,
    [`unitDocuments_${unitId}`],
    () => {
      setEditingDocId(null);
      setEditedTitle("");
    }
  );

 const {mutate: deleteDocumentFn} = useMutate(deleteDocument, [`unitDocuments_${unitId}`]);
  
  // Use actual documents if available, otherwise use mock data
  const displayDocuments = unitId && documents.length > 0 ? documents : [];

  const handleDocumentChange = (event) => {
    setSelectedDocument(!selectedDocument);
  };
  
  // Handle edit button click
  const handleEditClick = (doc) => {
    setEditingDocId(doc.id);
    setEditedTitle(doc.title);
  };
  
  // Handle save edit
  const handleSaveEdit = (docId) => {
    if (!editedTitle.trim()) {
      // Don't allow empty titles
      handleCancelEdit();
      return;
    }
    
    // Call the mutation function with the document ID and new title
    updateDocumentTitleFn({document_id:docId, data:{title: editedTitle, unit_id: unitId * 1}});
    
    // Note: we don't need to setEditingDocId(null) here
    // because it's handled in the onSuccess callback of the mutation
  };
  
  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingDocId(null);
    setEditedTitle('');
  };
  
  // Handle key press in the edit field
  const handleKeyPress = (e, docId) => {
    if (e.key === 'Enter') {
      handleSaveEdit(docId);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };
  // Handle opening document view popup
  const handleOpenViewPopup = (doc) => {
    setCurrentViewDoc(doc);
    setViewDocumentPopup(true);
  };

  // Handle closing document view popup
  const handleCloseViewPopup = () => {
    setViewDocumentPopup(false);
    setCurrentViewDoc(null);
  };
  
  // Handle opening delete confirmation dialog
  const handleOpenDeleteConfirm = (doc) => {
    setDocumentToDelete(doc);
    setDeleteConfirmOpen(true);
  };
  
  // Handle closing delete confirmation dialog
  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDocumentToDelete(null);
  };
  
  // Handle document deletion confirmation
  const handleConfirmDelete = () => {
    if (documentToDelete) {
      deleteDocumentFn({id:documentToDelete.id, unit_id: unitId * 1} );
      handleCloseDeleteConfirm();
    }
  };

  return (
    <div className="mt-2">
      {/* Document View Popup */}
      {viewDocumentPopup && currentViewDoc && (
        <LongFormDialog
          open={viewDocumentPopup}
          handleClose={handleCloseViewPopup}
          title={currentViewDoc.title || "Document View"}
        >
          <ViewFile url={currentViewDoc.file_path} />
        </LongFormDialog>
      )}
      
      {/* Delete Confirmation Dialog */}
      {deleteConfirmOpen && documentToDelete && (
        <ConfirmationDialog
          open={deleteConfirmOpen}
          handleClose={handleCloseDeleteConfirm}
          callback={handleConfirmDelete}
          title="Delete Document"
          btnTitle="Delete"
          btnColor="#E62B2A"
          message={`Are you sure you want to delete "${documentToDelete.title}"? This action cannot be undone.`}
        />
      )}

      <HeadingTitle
        title="View and manage documents for this unit"
        download={
          <Link onClick={handleDocumentChange} className="view-more-link">
            <p>{selectedDocument ? "Hide Documents" : "View More"}</p>{" "}
            <ArrowForwardIosIcon sx={{ fontSize: 12, ml: 0.5 }} />
          </Link>
        }
      >
        {selectedDocument && (
          <Paper
            elevation={0}
            sx={{ mt: 2, borderRadius: 2, overflow: "hidden" }}
          >
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress size={30} />
              </Box>
            ) : isError ? (
              <Typography color="error" sx={{ p: 2 }}>
                Error loading documents: {error?.message || "Unknown error"}
              </Typography>
            ) : (
              <List disablePadding sx={{ width: "100%" }}>
                {displayDocuments && displayDocuments.length ? (
                  displayDocuments.map((doc, index) => (
                    <React.Fragment key={doc.id}>
                      <ListItem
                        sx={{
                          py: 1.5,
                          px: 2,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          "&:hover": { bgcolor: "#f9f9f9" },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          sx={{ flexGrow: 1 }}
                        >
                          <img src={docIcon} alt="doc" />
                          {editingDocId === doc.id ? (
                            <TextField
                              value={editedTitle}
                              onChange={(e) => setEditedTitle(e.target.value)}
                              onKeyDown={(e) => handleKeyPress(e, doc.id)}
                              autoFocus
                              size="small"
                              fullWidth
                              sx={{
                                maxWidth: "400px",
                                outline: "none",
                                "& .MuiOutlinedInput-root": {
                                  "&.Mui-focused fieldset": {
                                    borderColor: "#11B50C",
                                    outline: "none",
                                  },
                                },
                              }}
                            />
                          ) : (
                            <div className="d-flex align-items-center">
                            <p className="title-details">{doc?.title}</p>
                            <p className="italic-date ml-2">({moment(doc?.uploaded_at).format("MM/DD/YYYY")})</p>
                            </div>
                          )}
                        </Stack>
                        <Stack direction="row" spacing={0.5}>
                          {editingDocId === doc.id ? (
                            <>
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#fff",
                                  backgroundColor: "#11B50C",
                                }}
                                onClick={() => handleSaveEdit(doc.id)}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#fff",
                                  backgroundColor: "#E62B2A",
                                }}
                                onClick={handleCancelEdit}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <IconButton
                                size="small"
                                sx={{
                                  backgroundColor: "#16304F",
                                  color: "#fff",
                                }}
                                onClick={() => handleOpenViewPopup(doc)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#fff",
                                  backgroundColor: "#355C9D",
                                }}
                                onClick={() => {
                                  download(
                                    process.env.REACT_APP_BASE_URL +
                                      doc.file_path,
                                    doc.file_name || "document"
                                  );
                                }}
                              >
                                <GetAppIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#fff",
                                  backgroundColor: "#11B50C",
                                }}
                                onClick={() => handleEditClick(doc)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{
                                  color: "#fff",
                                  backgroundColor: "#E62B2A",
                                }}
                                onClick={() => handleOpenDeleteConfirm(doc)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Stack>
                      </ListItem>
                      {index < displayDocuments.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                ) : (
                  <Typography variant="body1" className="loader-center">
                    No documents found
                  </Typography>
                )}
              </List>
            )}
          </Paper>
        )}
      </HeadingTitle>
    </div>
  );
}

export default ViewDocuments